
import React from "react";

// ========================================
// PRODUCT SUITABILITY & LIMITATIONS
// ========================================

const ProductSuitability = ({
    data = {},
    onChange
}) => {

    // ========================================
    // DATA
    // ========================================

    const suitability = {
        suitableConditions: [],
        unsuitableConditions: [],
        limitations: [],
        technicalReviewTriggers: [],
        ...(data || {})
    };


    // ========================================
    // STYLES
    // ========================================

    const inputClass =
        "w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-300";

    const addButtonClass =
        "px-4 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700";

    const removeButtonClass =
        "px-3 py-2 bg-red-50 text-red-700 border border-red-200 text-sm rounded hover:bg-red-100";


    // ========================================
    // UPDATE FIELD
    // ========================================

    const updateField = (field, value) => {

        onChange({
            ...suitability,
            [field]: value
        });

    };


    // ========================================
    // LIST MANAGEMENT
    // ========================================

    const addItem = (field) => {

        const items = Array.isArray(suitability[field])
            ? suitability[field]
            : [];

        updateField(
            field,
            [
                ...items,
                ""
            ]
        );

    };


    const updateItem = (
        field,
        index,
        value
    ) => {

        const items = Array.isArray(suitability[field])
            ? suitability[field]
            : [];

        updateField(
            field,
            items.map(
                (item, i) =>
                    i === index
                        ? value
                        : item
            )
        );

    };


    const removeItem = (
        field,
        index
    ) => {

        const items = Array.isArray(suitability[field])
            ? suitability[field]
            : [];

        updateField(
            field,
            items.filter(
                (_, i) => i !== index
            )
        );

    };


    // ========================================
    // REUSABLE SECTION
    // ========================================

    const renderSection = (
        field,
        title,
        description,
        placeholder,
        addLabel
    ) => {

        const items = Array.isArray(suitability[field])
            ? suitability[field]
            : [];

        return (

            <section className="space-y-5">

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 border-b pb-4">

                    <div>

                        <h3 className="text-base font-semibold text-gray-900">
                            {title}
                        </h3>

                        <p className="text-sm text-gray-500 mt-1">
                            {description}
                        </p>

                    </div>

                    <button
                        type="button"
                        onClick={() => addItem(field)}
                        className={addButtonClass}
                    >
                        + {addLabel}
                    </button>

                </div>


                {items.length === 0 && (

                    <div className="py-8 text-center border border-dashed rounded-lg">

                        <p className="text-sm text-gray-500">
                            No entries added yet.
                        </p>

                    </div>

                )}


                {items.map((item, index) => (

                    <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                    >

                        <div className="flex flex-col sm:flex-row gap-3">

                            <div className="flex-1 min-w-0">

                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {title} — {index + 1}
                                </label>

                                <textarea
                                    value={item || ""}
                                    onChange={(event) =>
                                        updateItem(
                                            field,
                                            index,
                                            event.target.value
                                        )
                                    }
                                    placeholder={placeholder}
                                    rows={3}
                                    className={inputClass}
                                />

                            </div>

                            <div className="sm:self-end">

                                <button
                                    type="button"
                                    onClick={() =>
                                        removeItem(field, index)
                                    }
                                    className={removeButtonClass}
                                >
                                    Remove
                                </button>

                            </div>

                        </div>

                    </div>

                ))}

            </section>

        );

    };


    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="space-y-10">

            {renderSection(
                "suitableConditions",
                "Suitable Conditions",
                "Describe conditions where this product is recommended for use.",
                "e.g. Suitable for the specified concrete substrate when surface preparation requirements are met.",
                "Suitable Condition"
            )}


            {renderSection(
                "unsuitableConditions",
                "Unsuitable Conditions",
                "Describe conditions where this product should not be recommended or applied.",
                "e.g. Not suitable for applications involving continuous water immersion unless specifically approved.",
                "Unsuitable Condition"
            )}


            {renderSection(
                "limitations",
                "Product Limitations",
                "Document restrictions, constraints and limitations of the product.",
                "e.g. Application temperature must remain within the manufacturer's specified range.",
                "Limitation"
            )}


            {renderSection(
                "technicalReviewTriggers",
                "Technical Review Triggers",
                "Identify situations where the AI agent should recommend consultation with the RAZON technical team before proceeding.",
                "e.g. Structural cracks or unusual site conditions requiring technical assessment.",
                "Review Trigger"
            )}


            <div className="border-t pt-4">

                <p className="text-xs text-gray-500">
                    These conditions help the AI agent avoid
                    unsuitable product recommendations and identify
                    situations requiring additional technical review.
                    Enter only verified product guidance.
                </p>

            </div>

        </div>

    );

};

export default ProductSuitability;
